import type { DesignType } from "~/interfaces/DesignEditor"

/**
 * Tipo de design (o "produto" que o usuário está criando).
 *
 * Escopo atual — quatro tipos:
 * - DIGITAL: imagem estática, medidas em px, página única.
 * - PRINT: material impresso, medidas em mm, uma ou mais páginas (faces),
 *   saída em JPG ou PDF de alta qualidade.
 * - PRESENTATION: slides em px, várias páginas, com animação.
 * - VIDEO: cenas em px, com transições, animação, áudio e vídeo.
 *
 * É ortogonal ao motor de edição: DIGITAL, PRINT e PRESENTATION usam o motor
 * estático (canvas); VIDEO usa o motor de timeline.
 */
export type ContentKind = "DIGITAL" | "PRINT" | "PRESENTATION" | "VIDEO"

/** Motor que renderiza o documento. */
export type EditorEngine = "STATIC" | "VIDEO"

/** Unidade de composição dentro do documento. */
export type ContentUnit = "page" | "slide" | "scene"

export interface ContentKindSpec {
  id: ContentKind
  engine: EditorEngine
  unit: ContentUnit
  /** Unidade das medidas do frame. */
  frameUnit: "px" | "mm"
  /** Aceita mais de uma página/slide/cena. */
  multipleUnits: boolean
  labelKey: string
  hintKey: string
  /** Aceita animação de entrada/saída. */
  animated: boolean
  /** Aceita faixa de áudio. */
  audio: boolean
  /** Aceita camadas de vídeo. */
  video: boolean
  /** Usa timeline com duração por cena. */
  timeline: boolean
  /** Cada unidade tem duração própria. */
  duration: boolean
  /** Trabalha com sangria (bleed) e área segura. */
  bleed: boolean
  /** Produto físico: PDF para impressão. */
  print: boolean
  /** Formatos de saída previstos. */
  exports: string[]
  /** Formato inicial quando o usuário cria do zero. */
  defaultFrame: { width: number; height: number }
}

export const CONTENT_KINDS: Record<ContentKind, ContentKindSpec> = {
  DIGITAL: {
    id: "DIGITAL",
    engine: "STATIC",
    unit: "page",
    frameUnit: "px",
    multipleUnits: false,
    labelKey: "kinds.digital.label",
    hintKey: "kinds.digital.hint",
    animated: false,
    audio: false,
    video: false,
    timeline: false,
    duration: false,
    bleed: false,
    print: false,
    exports: ["PNG", "JPG"],
    defaultFrame: { width: 1080, height: 1080 },
  },
  PRINT: {
    id: "PRINT",
    engine: "STATIC",
    unit: "page",
    frameUnit: "mm",
    multipleUnits: true,
    labelKey: "kinds.print.label",
    hintKey: "kinds.print.hint",
    animated: false,
    audio: false,
    video: false,
    timeline: false,
    duration: false,
    bleed: true,
    print: true,
    exports: ["JPG", "PDF"],
    defaultFrame: { width: 90, height: 50 },
  },
  PRESENTATION: {
    id: "PRESENTATION",
    engine: "STATIC",
    unit: "slide",
    frameUnit: "px",
    multipleUnits: true,
    labelKey: "kinds.presentation.label",
    hintKey: "kinds.presentation.hint",
    animated: true,
    audio: false,
    video: false,
    timeline: false,
    duration: false,
    bleed: false,
    print: false,
    exports: ["PDF", "PNG"],
    defaultFrame: { width: 1920, height: 1080 },
  },
  VIDEO: {
    id: "VIDEO",
    engine: "VIDEO",
    unit: "scene",
    frameUnit: "px",
    multipleUnits: true,
    labelKey: "kinds.video.label",
    hintKey: "kinds.video.hint",
    animated: true,
    audio: true,
    video: true,
    timeline: true,
    duration: true,
    bleed: false,
    print: false,
    exports: ["MP4", "WebM", "GIF"],
    defaultFrame: { width: 1920, height: 1080 },
  },
}

export const KIND_ORDER: ContentKind[] = ["DIGITAL", "PRINT", "PRESENTATION", "VIDEO"]

export const isContentKind = (value: unknown): value is ContentKind =>
  typeof value === "string" && (KIND_ORDER as string[]).includes(value)

/** Traduz o motor antigo (usado pelo roteador de editores) para o tipo de design. */
export const kindOfDesignType = (editorType: DesignType): ContentKind => {
  if (editorType === "VIDEO") return "VIDEO"
  if (editorType === "PRESENTATION") return "PRESENTATION"
  return "DIGITAL"
}

/** Escolhe o editor que sabe renderizar o tipo. */
export const designTypeOfKind = (kind: ContentKind): DesignType => {
  if (kind === "VIDEO") return "VIDEO"
  if (kind === "PRESENTATION") return "PRESENTATION"
  return "GRAPHIC"
}

export const isAnimatedKind = (kind: ContentKind) => CONTENT_KINDS[kind].animated

export const supportsDuration = (kind: ContentKind) => CONTENT_KINDS[kind].duration

export const supportsBleed = (kind: ContentKind) => CONTENT_KINDS[kind].bleed

export const supportsMultipleUnits = (kind: ContentKind) => CONTENT_KINDS[kind].multipleUnits

export const unitLabelKey = (kind: ContentKind) => `units.${CONTENT_KINDS[kind].unit}`

/** Tipos que usam o motor estático (canvas sem timeline). */
export const STATIC_ENGINE_KINDS: ContentKind[] = KIND_ORDER.filter(
  (kind) => CONTENT_KINDS[kind].engine === "STATIC"
)
